// Configuration for course content review
// Extracted to avoid import.meta.env transformation issues in Vue SFC files

export const CMS_BASE_URL = import.meta.env.VITE_CMS_BASE_URL;
export const FONTO_EDITOR_URL =
  import.meta.env.VITE_FONTO_EDITOR_URL || import.meta.env.VITE_FR_EDITOR_URL;
export const IS_DEV = import.meta.env.DEV || import.meta.env.VITE_ENV === 'Dev';
export const DEV_AUTH_TOKEN = import.meta.env.VITE_DEV_AUTH_TOKEN;

/**
 * DEV-only Okta bypass for the course-content-review E2E suite (the backend's `Bearer DEV1` ADMIN
 * mock, accepted by core only when ENVIRONMENT_NAME is dev/local). Gated three ways so it is
 * PROVABLY DEAD in production and not default-on for normal DEV users:
 *   1. `VITE_DEV_AUTH_TOKEN` must be set at build time. Prod/Non-Prod builds never set it, so the
 *      `!import.meta.env.VITE_DEV_AUTH_TOKEN` guard folds to `false` at compile time and the whole
 *      branch (token + every call site) is tree-shaken out of the bundle.
 *   2. On the local Vite dev server (`import.meta.env.DEV`) it is active by default. The ssp-e2e
 *      harness runs against the LOCAL stack, so the E2E keeps working with no extra opt-in.
 *   3. On a deployed DEV build it activates ONLY for a browser session that opted in via
 *      `localStorage['cocohub.devAuth'] === '1'`. Every other DEV session uses real Okta.
 * Must be read at CALL time (after the opt-in may be set), hence a function, not a const.
 */
export const DEV_AUTH_OPT_IN_KEY = 'cocohub.devAuth';

export function devAuthActive(): boolean {
  if (!import.meta.env.VITE_DEV_AUTH_TOKEN) return false;
  if (import.meta.env.DEV) return true;
  try {
    return globalThis.localStorage?.getItem(DEV_AUTH_OPT_IN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Large Revision workflow (CCH-315). The review-type selection (small vs large) is only offered
 * when `.env` contains exactly `VITE_FEATURE_LARGE_REVISION_ENABLED=true`. Any other value or
 * omitting the variable keeps large revision off and the flow defaults to small reviews.
 * Mirrors the backend FEATURE_LARGE_REVISION_ENABLED flag for trunk-based deployment.
 */
export const FEATURE_LARGE_REVISION_ENABLED =
  import.meta.env.VITE_FEATURE_LARGE_REVISION_ENABLED === 'true';

/**
 * AI Editor (CCH-449) — LOCAL DEV OVERRIDE ONLY. The backend `FEATURE_AI_EDITOR_ENABLED` flag is the
 * single source of truth: `useFontoEditor` reads it from `GET /ssp-api/v1/feature-flags` and opts the
 * Fonto session into the AI-Editor tab + Fonto Review sidebar accordingly. This env var only force-enables
 * the AI Editor locally in Vite dev mode (when `.env` contains exactly
 * `VITE_FEATURE_AI_EDITOR_ENABLED=true`) so you can test without flipping the backend; it can never
 * disable what the backend has enabled, and production builds ignore it.
 */
export const FEATURE_AI_EDITOR_DEV_OVERRIDE =
  import.meta.env.DEV && import.meta.env.VITE_FEATURE_AI_EDITOR_ENABLED === 'true';

/**
 * KFK Expert Revision (CCH-606/607/608). The EXPERT REVISION button and its
 * status polling are only shown when `.env` contains exactly
 * `VITE_FEATURE_KFK_EXPERT_REVISION_ENABLED=true`. Any other value or omitting
 * the variable keeps the feature off, so it ships dark to production until the
 * KFKSME board (on-behalf permission, statuses) is ready.
 */
export const FEATURE_KFK_EXPERT_REVISION_ENABLED =
  import.meta.env.VITE_FEATURE_KFK_EXPERT_REVISION_ENABLED === 'true';

/**
 * Playlist Management (CCH-671). The new playlist management experience on the Published Videos
 * page — the Playlists / Media Gallery tabs, the Media Gallery grid, and every mutation action
 * (create/rename/delete playlist, add/move/rename/delete video, download) — is only shown when
 * `.env` contains exactly `VITE_FEATURE_PLAYLIST_MANAGEMENT_ENABLED=true`. Any other value or
 * omitting the variable keeps the feature off, and the page falls back to the existing read-only
 * playlist carousel view. This ships the feature dark to production (enabled on Dev only) until it
 * is ready for release.
 */
export const FEATURE_PLAYLIST_MANAGEMENT_ENABLED =
  import.meta.env.VITE_FEATURE_PLAYLIST_MANAGEMENT_ENABLED === 'true';
